import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useDeploymentsApi } from '../../../apis/facades/useDeploymentsApi';
import { useFunnelMapApiFacade } from '../../../apis/facades/funnel-map/useFunnelMapApiFacade';
import { FunnelPatchPayload, useFunnelsApiFacade } from '../../../apis/facades/useFunnelsApiFacade';
import { setInitialFunnelMap } from '../../../reducers/united-canvas/unitedCanvasReducer';
import useCheckPermissionsForCurrentCrmOrg from '../../common/permissions/useCurrentCrmOrgPermissions';
import { normalizeFunnelMapPositions } from './normalizeFunnelMapPositions';
import { useSweepNotifications } from '../../notifications/useSweepNotifications';
import { telemetry } from '../../../telemetry';
import { SweepNotificationVariant } from '../../../reducers/notificationsReducer';
import {
  setFunnelStatusFromFunnel,
  setMultipleFunnelStatuses,
} from '../../../reducers/funnelStatusReducer';

type SaveFunnelDataProps = {
  id: string;
  crmOrgId?: string | null;
} & FunnelPatchPayload;

export const useFunnelMapFlowPageApi = () => {
  const dispatch = useDispatch();
  const { addNotification } = useSweepNotifications();

  const { get_funnelMap } = useFunnelMapApiFacade();
  const { patch_funnel } = useFunnelsApiFacade();
  const { post_deploymentsCrmOrg } = useDeploymentsApi();
  const [isAllowedSaveFunnel] = useCheckPermissionsForCurrentCrmOrg(['edit:funnels']);

  const loadFunnelMap = useCallback(
    async (funnelMapId: string) => {
      const funnelMap = (await get_funnelMap(funnelMapId, true)) as FunnelMap;

      dispatch(setInitialFunnelMap(normalizeFunnelMapPositions(funnelMap)));
      dispatch(setMultipleFunnelStatuses(Object.values(funnelMap.funnelsData)));
      return funnelMap;
    },
    [dispatch, get_funnelMap],
  );

  const saveFunnelData = useCallback(
    async (props: SaveFunnelDataProps) => {
      const { id, crmOrgId, ...payload } = props;
      const funnel = await patch_funnel(id, payload, crmOrgId);
      dispatch(setFunnelStatusFromFunnel(funnel));
      return funnel;
    },
    [dispatch, patch_funnel],
  );

  const deployFunnelDetail = async ({
    funnelId,
    snapshotId,
    orgId,
  }: {
    funnelId?: string;
    snapshotId?: string;
    orgId: string;
  }) => {
    if (!funnelId) {
      telemetry.captureError(
        new Error(`funnelId not present. snapshotId: ${snapshotId} orgId: ${orgId}`),
      );
      addNotification({
        message: 'Error deploying funnel.',
        variant: SweepNotificationVariant.Error,
      });
      return;
    }
    return post_deploymentsCrmOrg({
      crmOrgId: orgId,
      payload: { funnelId, snapshotId },
    });
  };

  const saveAndDeployFunnelDetail = async ({
    funnelId,
    snapshotId,
    funnelDetail,
    orgId,
    onFailedSaveCallback,
  }: {
    snapshotId?: string;
    funnelId: string;
    orgId: string;
    funnelDetail: FunnelDetails;
    onFailedSaveCallback: () => void;
  }) => {
    if (isAllowedSaveFunnel) {
      try {
        await saveFunnelData({ id: funnelId, funnelDetails: funnelDetail, crmOrgId: orgId });
      } catch (e) {
        onFailedSaveCallback();
        return;
      }
    }
    await deployFunnelDetail({
      funnelId,
      snapshotId,
      orgId,
    });
  };

  return { loadFunnelMap, saveFunnelData, saveAndDeployFunnelDetail };
};
